import { createApiClient, unwrap } from '@barahpay/shared';
import type { CheckoutResult, CheckoutSession, StartCheckoutResponse } from '@barahpay/shared';

/**
 * Only public configuration reaches the browser bundle — no secrets (spec section 65).
 */
const baseUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:5150';

let currentLanguage = 'en';

export function setApiLanguage(language: string): void {
  currentLanguage = language;
}

const client = createApiClient({
  baseUrl,
  getLanguage: () => currentLanguage,
});

/** The checkout API is keyed entirely by the opaque public token (spec section 18). */
export const checkoutApi = {
  async getSession(token: string): Promise<CheckoutSession> {
    const response = await client.get(`/api/v1/checkout/${encodeURIComponent(token)}`);
    return unwrap<CheckoutSession>(response.data);
  },

  async start(token: string, paymentMethod?: string, amount?: number): Promise<StartCheckoutResponse> {
    const response = await client.post(`/api/v1/checkout/${encodeURIComponent(token)}/start`, {
      paymentMethod,
      amount,
      language: currentLanguage,
    });

    return unwrap<StartCheckoutResponse>(response.data);
  },

  /**
   * `resourcePath` is whatever the provider put on the URL it returned the payer to, passed through
   * untouched. Only its presence matters: it is how the server tells the payer's return from the
   * page's own polling. The server never uses its value to decide what to look up.
   */
  async getResult(token: string, resourcePath?: string): Promise<CheckoutResult> {
    const response = await client.get(`/api/v1/checkout/${encodeURIComponent(token)}/result`, {
      params: { lang: currentLanguage, resourcePath },
    });

    return unwrap<CheckoutResult>(response.data);
  },
};
